import { useMemo, useState } from "react";

import { Search } from "lucide-react";

import { Input } from "@components/ui/input";
import { MultiSelect } from "@components/ui/multi-select";
import { ScrollArea } from "@components/ui/scroll-area";

import {
  getCocCategories,
  getCodeOfConductEntriesForCategories,
} from "@constants/codeOfConduct";

import { useCodeOfConduct } from "@hooks/queries/use-code-of-conduct";

import { EntryCard } from "./EntryCard";

/**
 * Right-rail panel for browsing Code of Conduct declarations on the Report
 * Builder page (MDRS-139).
 *
 * The Report Builder no longer auto-inserts CoC content (MDRS-140). This panel
 * lets the expert review the correct wording for their category (jurisdiction
 * or service) and copy the paragraph into the draft. Content is served by the
 * backend (GET /report-generator/code-of-conduct) via `useCodeOfConduct`.
 *
 * Fully self-contained (no props) — the Report Builder closes it by selecting a
 * different section, so the panel owns no close affordance of its own.
 */
export const CodeOfConductPanel = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const { data: allEntries, isLoading, isError } = useCodeOfConduct();

  const categoryOptions = useMemo(
    () => getCocCategories(allEntries ?? []),
    [allEntries]
  );

  // A search query always searches the full set — it ignores the category
  // filter so the expert can find any declaration by typing, without first
  // clearing the dropdown. When the search box is empty we fall back to the
  // category filter (empty selection = show all).
  const entries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) {
      return (allEntries ?? []).filter(
        (e) =>
          e.label.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q) ||
          e.serviceKind.toLowerCase().includes(q) ||
          e.text.toLowerCase().includes(q)
      );
    }
    return getCodeOfConductEntriesForCategories(categories, allEntries ?? []);
  }, [categories, search, allEntries]);

  // Typing a search resets the category dropdown to its default (all) so the
  // two filters don't visually contradict each other while search spans
  // everything.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (value.trim() && categories.length > 0) {
      setCategories([]);
    }
  };

  return (
    <aside className="flex h-full flex-col overflow-hidden bg-background-white">
      <div className="flex flex-col gap-3 px-4 pt-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-muted" />
          <Input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search Sectors & Schemes"
            aria-label="Search Code of Conduct content"
            className="h-9 pl-8 text-xs"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-foreground-muted">Filter by:</p>
          <MultiSelect
            options={categoryOptions}
            selected={categories}
            onChange={setCategories}
            maxDisplay={1}
            placeholder="Jurisdiction/service"
            searchPlaceholder="Search…"
            ariaLabel="Filter by jurisdiction or service"
          />
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1 px-4 pt-3 [&>[data-slot=scroll-area-viewport]>div]:!block">
        {isLoading ? (
          <p className="py-8 text-center text-xs text-foreground-muted">
            Loading Code of Conduct declarations…
          </p>
        ) : isError ? (
          <p className="py-8 text-center text-xs text-destructive">
            Couldn&apos;t load Code of Conduct declarations. Try again shortly.
          </p>
        ) : entries.length === 0 ? (
          <p className="py-8 text-center text-xs text-foreground-muted">
            No Code of Conduct entries match your filter.
          </p>
        ) : (
          // Dynamic grid: cards flow into as many columns as the panel width
          // allows (1 at the default/narrow width, more as it's dragged wider).
          // A ~320px track keeps cards comfortably wide on a wide panel (fewer,
          // roomier columns); `min(320px, 100%)` lets the single column shrink
          // with the panel on narrow widths instead of overflowing. Cards
          // stretch to equal height per row (grid default) so a wide layout
          // reads as even rows.
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(320px,100%),1fr))] gap-2">
            {entries.map((entry) => (
              <EntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </ScrollArea>
    </aside>
  );
};
